/** Peer-vouch landing page.
 *
 *  A verified peer clicks their vouch link (contains a token), sees who
 *  they'd be vouching for, and confirms. When 2 of 3 references vouch,
 *  the pending individual is auto-verified.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, UserCheck, XCircle } from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;

export default function Vouch() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, target: null, error: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/individuals/vouch/${token}`)
      .then((r) => setState({ loading: false, target: r.data, error: "" }))
      .catch((e) => setState({
        loading: false, target: null,
        error: e.response?.data?.detail || "Invalid or expired vouch link",
      }));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/individuals/vouch/${token}`);
      setDone(data);
      toast.success(
        data.individual_verified
          ? "Vouch recorded — the individual is now verified."
          : `Vouch recorded (${data.vouched_count}/3).`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record vouch");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center text-white/60"
           data-testid="vouch-loading">Loading vouch…</div>
    );
  }
  if (state.error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="vouch-error">
        <XCircle size={28} className="text-red-400 mx-auto mb-3" />
        <h1 className="font-display text-3xl uppercase">Link Invalid</h1>
        <p className="text-sm text-white/60 mt-3">{state.error}</p>
      </div>
    );
  }

  const t = state.target;

  return (
    <div className="mx-auto max-w-2xl px-4 py-16" data-testid="vouch-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Peer Vouch</div>
      <h1 className="font-display text-4xl uppercase tracking-tight mt-2">
        Vouch for a Peer
      </h1>

      <div className="cs-card rounded-2xl p-6 mt-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/10">
            <UserCheck size={22} />
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl">{t.name}</div>
            <div className="text-xs text-white/55 mt-1">{t.email}</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mt-2">
              {t.kind_label} · {t.designation || "—"}
            </div>
            <div className="mt-4 text-sm text-white/75 leading-relaxed">
              By vouching, you confirm this person genuinely works as a{" "}
              <span className="font-semibold">{t.kind_label}</span>. When 2 out
              of 3 references vouch, they&apos;re auto-verified on the platform.
            </div>
          </div>
        </div>

        {done ? (
          <div className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4"
               data-testid="vouch-success">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-sm">
                  {done.individual_verified
                    ? "Verified — thanks for vouching!"
                    : `Vouch recorded (${done.vouched_count}/3)`}
                </div>
                <div className="text-xs text-white/60 mt-1">
                  {done.individual_verified
                    ? `${t.name} is now a verified member.`
                    : "They still need at least 2 vouches to auto-verify."}
                </div>
                <Button onClick={() => nav("/dashboard")}
                        className="mt-3 h-9 text-xs uppercase tracking-[0.16em]"
                        data-testid="vouch-go-dashboard">
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 flex gap-3">
            <Button onClick={submit} disabled={busy}
                    className="h-10 px-5 text-xs uppercase tracking-[0.16em]"
                    data-testid="vouch-confirm-button">
              {busy ? "Vouching…" : "Yes, I vouch for this person"}
            </Button>
            <Button variant="outline" onClick={() => nav("/dashboard")}
                    className="h-10 px-5 text-xs uppercase tracking-[0.16em] border-white/20"
                    data-testid="vouch-cancel-button">
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
